import { U2000ApiValueToDisplayYmd, u2000ShiftCivilYmd } from "./U2000DateTimeHelpers";

/** Jours saisissables sur les lignes honoraires / banques (U3129 / U3130). */
export const U2000_TIMESHEET_WEEK_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

export type U2000TimesheetWeekDay = (typeof U2000_TIMESHEET_WEEK_DAYS)[number];

export type U2000HonorariumDayTotals = {
  daySums: Record<U2000TimesheetWeekDay, number>;
  grandTotal: number;
};

function emptyDaySums(): Record<U2000TimesheetWeekDay, number> {
  return {
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  };
}

function dayHours(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Somme les heures par jour sur un ensemble de lignes (honoraires projet ou banques).
 * `grandTotal` inclut les sept jours (comportement historique des pieds de tableau).
 */
export function u2000SumHonorariumDayTotals(
  rows: ReadonlyArray<Partial<Record<U2000TimesheetWeekDay, unknown>>> | undefined | null,
): U2000HonorariumDayTotals {
  const daySums = emptyDaySums();
  let grandTotal = 0;
  for (const row of rows ?? []) {
    for (const d of U2000_TIMESHEET_WEEK_DAYS) {
      const v = dayHours(row[d]);
      daySums[d] += v;
      grandTotal += v;
    }
  }
  return { daySums, grandTotal };
}

/** Jours affichés selon la case « Afficher week-end ». */
export function u2000TimesheetVisibleWeekDays(showWeekend: boolean): readonly U2000TimesheetWeekDay[] {
  return showWeekend ? U2000_TIMESHEET_WEEK_DAYS : U2000_TIMESHEET_WEEK_DAYS.filter((d) => d !== "saturday" && d !== "sunday");
}

/** Dates ISO (YYYY-MM-DD) par jour de semaine, dérivées du lundi `periodDate`. */
export function u2000TimesheetWeekDayIsoDates(periodDate: string | undefined | null): Record<U2000TimesheetWeekDay, string> | null {
  const mondayYmd = U2000ApiValueToDisplayYmd(periodDate);
  if (!mondayYmd) return null;
  const dates = {} as Record<U2000TimesheetWeekDay, string>;
  U2000_TIMESHEET_WEEK_DAYS.forEach((d, i) => {
    dates[d] = u2000ShiftCivilYmd(mondayYmd, i) ?? mondayYmd;
  });
  return dates;
}

/** Liste ordonnée des sept dates ISO de la semaine (lundi → dimanche). */
export function u2000TimesheetWeekIsoDateList(periodDate: string | undefined | null): string[] | null {
  const map = u2000TimesheetWeekDayIsoDates(periodDate);
  if (!map) return null;
  return U2000_TIMESHEET_WEEK_DAYS.map((d) => map[d]);
}

/** Somme des totaux journaliers sur les colonnes visibles uniquement (pied croisé optionnel). */
export function u2000SumHonorariumDayTotalsForVisibleDays(
  daySums: Record<U2000TimesheetWeekDay, number>,
  visibleDays: readonly U2000TimesheetWeekDay[],
): number {
  return visibleDays.reduce((sum, d) => sum + (daySums[d] ?? 0), 0);
}
